import { createElement } from 'react';
import ExpandMore from '@mui/icons-material/ExpandMore';
import colors from './colors';
import {
  PANEL_HEADER_MIN_HEIGHT,
  getPanelBodyStyles,
  getPanelHeaderStyles,
  getPanelSurfaceStyles,
} from './panelStyles';

const BTN_SMALL = 30;
const BTN_MEDIUM = 40;
const BTN_LARGE = 56;

const buttonSize = (size) => ({
  minHeight: size,
  minWidth: size,
  height: size,
});

export const components = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      body: {
        color: theme.palette.text.primary,
      },
      '.react-datepicker': {
        backgroundColor: `${theme.palette.background.paper}`,
        color: `${theme.palette.text.primary}`,
        borderColor: `${theme.palette.action.disabled}`,
      },
      '.react-datepicker__header': {
        backgroundColor: `${theme.palette.background.grey}`,
        borderColor: `${theme.palette.divider}`,
      },
      '.react-datepicker__time-container': {
        borderColor: `${theme.palette.action.disabled}`,
      },
      '.react-datepicker__current-month, .react-datepicker__day-name, .react-datepicker-time__header, .react-datepicker__day, .react-datepicker__time-list-item':
        {
          color: `${theme.palette.text.primary}`,
        },
      '.react-datepicker__day:hover': {
        backgroundColor: `${theme.palette.action.hover}`,
      },
      '.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item:hover':
        {
          backgroundColor: `${theme.palette.action.hover}`,
        },
      '.react-datepicker__day--selected, .react-datepicker__day--keyboard-selected, .react-datepicker__day--selected:hover, .react-datepicker__day--keyboard-selected:hover':
        {
          backgroundColor: `${theme.palette.primary.main}`,
          color: `${theme.palette.primary.contrastText}`,
        },
      '.react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected, .react-datepicker__time-container .react-datepicker__time .react-datepicker__time-box ul.react-datepicker__time-list li.react-datepicker__time-list-item--selected:hover':
        {
          backgroundColor: `${theme.palette.primary.main}`,
          color: `${theme.palette.primary.contrastText}`,
        },
      '.react-datepicker__day--disabled': {
        color: `${theme.palette.action.disabled}`,
      },
      '.react-datepicker__time-container .react-datepicker__time': {
        backgroundColor: `${theme.palette.background.paper}`,
        scrollbarColor:
          theme.palette.mode === 'dark'
            ? `${theme.palette.divider} ${theme.palette.divider}`
            : `${theme.palette.divider} ${theme.palette.lightGrey}`,
      },
      '.react-datepicker-time__input input': {
        backgroundColor: `${theme.palette.background.paper}`,
        color: `${theme.palette.text.primary}`,
        border: `1px solid ${theme.palette.action.disabled}`,
      },
      '.react-datepicker-time__input input[type="time"]::-webkit-calendar-picker-indicator': {
        filter: theme.palette.mode === 'dark' ? 'invert(1) opacity(1)' : 'opacity(1)',
      },
      '.react-datepicker__navigation-icon::before': {
        borderColor: `${theme.palette.lightGrey}`,
      },
    }),
  },
  MuiAccordion: {
    defaultProps: {
      disableGutters: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...getPanelSurfaceStyles(theme),
        // MUI sets its own radii on first/last accordions, so every corner is restated here.
        '&:first-of-type': {
          borderTopLeftRadius: theme.spacing(1),
          borderTopRightRadius: theme.spacing(1),
          borderBottomLeftRadius: theme.spacing(1),
          borderBottomRightRadius: theme.spacing(1),
        },
        '&:last-of-type': {
          borderTopLeftRadius: theme.spacing(1),
          borderTopRightRadius: theme.spacing(1),
          borderBottomLeftRadius: theme.spacing(1),
          borderBottomRightRadius: theme.spacing(1),
        },
        '&::before': {
          display: 'none',
        },
      }),
    },
  },
  MuiAccordionSummary: {
    defaultProps: {
      expandIcon: createElement(ExpandMore, { sx: { color: 'text.primary' } }),
    },
    styleOverrides: {
      root: ({ theme }) => ({
        ...getPanelHeaderStyles(theme),
        '&.Mui-expanded': {
          minHeight: PANEL_HEADER_MIN_HEIGHT,
        },
      }),
      content: ({ theme }) => ({
        '&, &.Mui-expanded': {
          margin: 0,
        },
        ...theme.typography.subtitle2,
        color: theme.palette.text.primary,
      }),
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...getPanelBodyStyles(theme),
        borderBottomLeftRadius: theme.spacing(1),
        borderBottomRightRadius: theme.spacing(1),
      }),
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        [theme.breakpoints.down('sm')]: {
          userSelect: 'none',
        },
        '&.MuiTypography-gutterBottom': {
          marginBottom: '0.5em',
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.action.disabledBackground}`,
      }),
      indicator: ({ theme }) => ({
        height: '2px',
        backgroundColor: theme.palette.text.primary,
      }),
    },
  },
  MuiBreadcrumbCollapsed: {
    variants: [
      {
        props: {},
        style: ({ theme }) => ({
          backgroundColor: 'transparent',
          color: theme.palette.text.secondary,
        }),
      },
    ],
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        padding: '9px 16px',
        ...theme.typography.buttonSmall,
        '&.Mui-selected': {
          color: theme.palette.text.primary,
        },
        '&.Mui-disabled': {
          color: theme.palette.action.disabled,
        },
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      size: 'medium',
    },
    styleOverrides: {
      sizeSmall: ({ theme }) => ({
        ...buttonSize(BTN_SMALL),
        ...theme.typography.buttonSmall,
      }),
      sizeMedium: ({ theme }) => ({
        ...buttonSize(BTN_MEDIUM),
        ...theme.typography.buttonMedium,
      }),
      sizeLarge: ({ theme }) => ({
        ...buttonSize(BTN_LARGE),
        ...theme.typography.buttonLarge,
      }),
      outlined: ({ ownerState, theme }) => {
        const isPrimaryVariant =
          !ownerState.color || ownerState.color === 'primary' || ownerState.color === 'secondary';
        const color = isPrimaryVariant
          ? theme.palette.text.secondary
          : (theme.palette[ownerState.color]?.main ?? ownerState.color);
        return {
          color,
          borderColor: color,
          '&:hover': {
            borderColor: color,
            background: 'rgba(0, 0, 0, 0.04)',
          },
        };
      },
      text: ({ ownerState, theme }) => ({
        ...(!ownerState.color || ownerState.color === 'primary' ? { color: theme.palette.secondary.main } : {}),
      }),
    },
  },
  MuiIconButton: {
    defaultProps: {
      size: 'medium',
    },
    styleOverrides: {
      sizeSmall: {
        ...buttonSize(BTN_SMALL),
        width: BTN_SMALL,
      },
      sizeMedium: {
        ...buttonSize(BTN_MEDIUM),
        width: BTN_MEDIUM,
      },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        minHeight: '40px',
        '&.Mui-disabled.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[400],
        },
        '&.Mui-disabled .MuiInputAdornment-root, &.Mui-disabled .MuiSvgIcon-root': {
          color: theme.palette.grey[500],
        },
        '&.MuiInputBase-multiline textarea': {
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.action.disabled,
            borderRadius: '3px',
          },
        },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 8,
        borderColor: theme.palette.grey[400],
      }),
      sizeSmall: buttonSize(BTN_SMALL),
      sizeMedium: buttonSize(BTN_MEDIUM),
    },
    variants: [
      {
        props: { variant: 'primary' },
        style: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              backgroundColor: theme.palette.grey[400],
              color: theme.palette.grey[600],
            },
          },
        }),
      },
      {
        props: { variant: 'filter' },
        style: ({ ownerState, theme }) => ({
          '&.MuiToggleButton-root': {
            borderColor: `${theme.palette[ownerState.color]?.main ?? ownerState.color} !important`,
          },
          '&.Mui-selected': {
            backgroundColor: 'currentColor',
            '&:hover': {
              backgroundColor: 'currentColor',
            },
            '&.Mui-selected *': {
              color: 'white !important',
            },
          },
        }),
      },
    ],
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        overflow: 'hidden',
      },
    },
  },
  MuiDialogTitle: {
    defaultProps: {
      variant: 'h3',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: 16,
        borderTop: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        margin: 24,
        padding: 0,
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 32,
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: {
        fontSize: '1rem',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        ...(ownerState.severity && {
          color: theme.palette.text?.[ownerState.severity] ?? theme.palette.text.primary,
          backgroundColor: theme.palette?.[ownerState.severity].background,
        }),
        ...(ownerState.variant === 'outlined' && {
          boxShadow: theme.shadows[4],
        }),
        '& a': {
          color: 'inherit',
        },
        '& .MuiAlertTitle-root': {
          fontWeight: 600,
        },
      }),
      message: {
        '& .MuiAlertTitle-root': {
          marginBottom: '4px !important',
        },
      },
    },
  },
  MuiDataGrid: {
    styleOverrides: {
      root: {
        backgroundColor: '#FFFFFF',
      },
      columnHeaderTitle: { fontWeight: 600 },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: colors.grey[1000],
        fontSize: '0.75rem',
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        textDecorationColor: 'currentColor',
        '&:hover': {
          opacity: 0.85,
        },
        '&.MuiButton-root': {
          textTransform: 'none',
          '&:hover': {
            opacity: 1,
            textDecoration: 'underline',
          },
        },
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.grey[700],
      }),
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:has(.MuiRadio-root)': {
          height: '40px',
          paddingRight: '8px',
          overflow: 'hidden',
        },
        '&:has(.MuiRadio-root) .MuiFormControlLabel-label': {
          fontSize: '16px',
          lineHeight: '24px',
          fontWeight: 400,
          color: theme.palette.text.secondary,
        },
        '&:has(.MuiRadio-root.Mui-checked) .MuiFormControlLabel-label': {
          color: theme.palette.text.primary,
        },
        '&:has(.MuiRadio-root.Mui-disabled) .MuiFormControlLabel-label': {
          color: theme.palette.action.disabled,
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 999,
        '& .MuiChip-deleteIcon, & .MuiChip-icon, & .MuiSvgIcon-root': {
          fontSize: '1.125rem',
        },
        padding: 6,
        '.MuiSvgIcon-root': {
          margin: '0 -4px 0 8px',
        },
        '.MuiChip-label': {
          padding: '0 8px 0 8px',
        },
        '.MuiChip-icon': {
          margin: '0 0 0 4px',
        },
        '.MuiChip-deleteIcon': {
          margin: '0 4px 0 0',
        },
      },
    },
    variants: [
      {
        props: { size: 'small' },
        style: {
          padding: 4,
          '.MuiSvgIcon-root': {
            margin: '0 -4px 0 4px',
          },
          '.MuiChip-label': {
            padding: '0 4px 0 4px',
          },
          '.MuiChip-icon': {
            margin: 0,
          },
          '.MuiChip-deleteIcon': {
            margin: 0,
          },
        },
      },
      {
        props: { size: 'large' },
        style: {
          height: 40,
          fontSize: '14px',
          padding: 8,
        },
      },
    ],
  },
  InfoPopover: {
    defaultProps: {
      minWidth: '245px',
      bodyVariant: 'caption',
      backgroundColor: 'background.default',
      closeButtonProps: { size: 'small', sx: { right: '6px' }, iconSx: { fontSize: '16px' } },
    },
  },
};
